import React, { useEffect, useRef } from 'react';
import {
    Button,
    Card,
    CardBody,
    CardTitle,
    PageSection,
} from '@patternfly/react-core';

interface VideoSectionProps {
    videoRef: React.RefObject<HTMLVideoElement>;
    cameraTracks: string[];
    activeCamera: string;
    onCameraChange: (trackName: string) => void;
    onCameraCycle: (direction: 'prev' | 'next') => void;
}

const SWIPE_THRESHOLD = 70;
const SWIPE_RATIO = 1.3;
const WHEEL_THRESHOLD = 36;
const SWIPE_COOLDOWN_MS = 420;

export const VideoSection: React.FC<VideoSectionProps> = ({
    videoRef,
    cameraTracks,
    activeCamera,
    onCameraChange,
    onCameraCycle
}) => {
    const swipeRef = useRef<{ x: number; y: number; active: boolean; touchId: number | null }>({
        x: 0,
        y: 0,
        active: false,
        touchId: null,
    });
    const lastSwipeAtRef = useRef(0);

    const canSwitchCamera = cameraTracks.length > 1;

    const formatCameraLabel = (name: string) => {
        if (!name) {
            return 'Camera';
        }
        const label = name.replace(/^cam_/, '').replace(/_/g, ' ');
        return label.toUpperCase();
    };

    useEffect(() => {
        if (!cameraTracks.length) {
            return;
        }
        if (!cameraTracks.includes(activeCamera)) {
            onCameraChange(cameraTracks[0]);
        }
    }, [cameraTracks, activeCamera, onCameraChange]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (!event.ctrlKey) {
                return;
            }
            if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') {
                return;
            }
            const target = event.target as HTMLElement | null;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
                return;
            }
            event.preventDefault();
            onCameraCycle(event.key === 'ArrowRight' ? 'next' : 'prev');
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [onCameraCycle]);

    const triggerCameraSwitch = (direction: 'prev' | 'next') => {
        if (!canSwitchCamera) {
            return;
        }
        const now = Date.now();
        if (now - lastSwipeAtRef.current < SWIPE_COOLDOWN_MS) {
            return;
        }
        lastSwipeAtRef.current = now;
        onCameraCycle(direction);
    };

    const beginSwipe = (x: number, y: number, touchId: number | null = null) => {
        swipeRef.current = { x, y, active: true, touchId };
    };

    const cancelSwipe = () => {
        swipeRef.current.active = false;
        swipeRef.current.touchId = null;
    };

    const finishSwipe = (x: number, y: number) => {
        if (!swipeRef.current.active) {
            return;
        }
        const dx = x - swipeRef.current.x;
        const dy = y - swipeRef.current.y;
        cancelSwipe();
        if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy) * SWIPE_RATIO) {
            triggerCameraSwitch(dx < 0 ? 'next' : 'prev');
        }
    };

    return (
        <PageSection>
            <Card>
                <CardTitle>Video Stream</CardTitle>
                <CardBody>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '640px',
                            height: '480px',
                            position: 'relative'
                        }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                muted
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'contain',
                                    borderRadius: '8px',
                                    backgroundColor: '#000'
                                }}
                                onMouseDown={(event) => {
                                    if (!canSwitchCamera) {
                                        return;
                                    }
                                    if (event.button !== 0) {
                                        return;
                                    }
                                    beginSwipe(event.clientX, event.clientY);
                                }}
                                onMouseUp={(event) => {
                                    if (!canSwitchCamera) {
                                        return;
                                    }
                                    finishSwipe(event.clientX, event.clientY);
                                }}
                                onMouseLeave={() => {
                                    cancelSwipe();
                                }}
                                onTouchStart={(event) => {
                                    if (!canSwitchCamera) {
                                        return;
                                    }
                                    const touch = event.changedTouches[0];
                                    if (!touch) {
                                        return;
                                    }
                                    beginSwipe(touch.clientX, touch.clientY, touch.identifier);
                                }}
                                onTouchEnd={(event) => {
                                    if (!canSwitchCamera) {
                                        return;
                                    }
                                    if (!swipeRef.current.active) {
                                        return;
                                    }
                                    const touch = Array.from(event.changedTouches).find(
                                        (entry) => entry.identifier === swipeRef.current.touchId
                                    );
                                    if (!touch) {
                                        cancelSwipe();
                                        return;
                                    }
                                    finishSwipe(touch.clientX, touch.clientY);
                                }}
                                onTouchCancel={() => {
                                    cancelSwipe();
                                }}
                                onWheel={(event) => {
                                    if (!canSwitchCamera) {
                                        return;
                                    }
                                    if (Math.abs(event.deltaX) < WHEEL_THRESHOLD || Math.abs(event.deltaX) < Math.abs(event.deltaY)) {
                                        return;
                                    }
                                    event.preventDefault();
                                    triggerCameraSwitch(event.deltaX > 0 ? 'next' : 'prev');
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                left: '50%',
                                bottom: '12px',
                                transform: 'translateX(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <Button
                                    variant="secondary"
                                    isDisabled={!canSwitchCamera}
                                    onClick={() => triggerCameraSwitch('prev')}
                                    aria-label="Previous camera"
                                >
                                    ◀ Prev
                                </Button>
                                <div style={{
                                    padding: '8px 16px',
                                    borderRadius: '999px',
                                    backgroundColor: 'rgba(0,0,0,0.75)',
                                    color: '#fff',
                                    fontSize: '11px',
                                    letterSpacing: '0.18em'
                                }}>
                                    {formatCameraLabel(activeCamera)}
                                </div>
                                <Button
                                    variant="secondary"
                                    isDisabled={!canSwitchCamera}
                                    onClick={() => triggerCameraSwitch('next')}
                                    aria-label="Next camera"
                                >
                                    Next ▶
                                </Button>
                            </div>
                        </div>
                    </div>
                </CardBody>
            </Card>
        </PageSection>
    );
};
